/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarNavLink';

const SidebarNavLinkManager: ComponentManagerFn<'SidebarNavLink'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarNavLink'>(platform, 'SidebarNavLink'),
    type: 'SidebarNavLink',
    name: 'Sidebar Nav Link',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      name: {
        label: 'Name',
        type: 'expression',
        returnType: async () => 'string',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
      },
      iconColorExpression: {
        label: 'Icon Color',
        type: 'ColorExpression',
        placeholder: 'e.g. #a2a0a2 or blue or primary',
      },
      iconString: {
        label: 'Icon string',
        type: 'string',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'iconString':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.name.value ?? '',
    category: 'Sidebar',
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Sidebar Nav Link is a specialized navigation component designed for sidebar menus. Similar to the SidebarListItem, it displays a row with an icon and text. However, it is specifically optimized for dynamic navigation scenarios where the label might need to change programmatically.
Use this component when building main application menus where the link text depends on variables or user context.

**1. Configuration**<br/>
* **Name:** The text label for the link. Unlike the static string in SidebarListItem, this property is an Expression, allowing the text to be dynamic (e.g., "Inbox (" + $._.unreadCount + ")").
* **Icon:** Select a standard icon from the library.
* **Icon Color:** Supports ColorExpression to dynamically style the icon (e.g., turning red if an alert condition is met).
* **Icon String:** A hidden property (isPropertyVisible: false) likely used for internal icon management or legacy support.

**2. Interactivity**<br/>
* **TriggerOnClick:** Supports adding a TriggerOnClick child to define the navigation action (e.g., "Go to Page X") when the link is clicked.

**3. Comparison: Nav Link vs. List Item**<br/>
* **Label Type:** Sidebar Nav Link uses an Expression (Dynamic), while Sidebar List Item uses a String (Static).
* **Primary Use:** Nav Link is for navigation that may update (e.g., "User Profile", "My Tasks (5)"), whereas List Item is for static menu options (e.g., "Home", "Settings").

**4. Best Use Cases**<br/>
* **User-Specific Links:** "My Profile" vs. "Login" depending on authentication state.
* **Badged Navigation:** Showing counts or status in the menu text (e.g., "Approvals (3)").
* **Localized Menus:** Using expressions to translate menu item names based on the user's selected language.`,
  };
};

export default SidebarNavLinkManager;
